import {
    Box,
    MuiIcons,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from "@leap4/frontend";
import React from "react";
import type { ExplorerResultDto, ExplorerRowDto } from "../../../../api/generated/models";
import { DASH, formatMetric, formatNumber, formatPercent, signColor } from "../format";

const { KeyboardArrowRight } = MuiIcons;

/** Columns the backend will sort on. Anything else stays a display-only cell. */
type SortKey =
    | "impressions" | "cost" | "costShare" | "visits" | "conversions" | "revenue"
    | "profit" | "cpa" | "roi" | "roas" | "margin" | "grossRevenue" | "takeRate" | "cpm" | "rpm" | "edgePer1k";

interface IColumn {
    key: string;
    label: string;
    sortKey?: SortKey;
    align?: "left" | "right";
    width?: number;
    title?: string;
    render: (r: ExplorerRowDto) => React.ReactNode;
}

interface IProps {
    result: ExplorerResultDto;
    conversionLabel: string;
    rpmLabel: string;
    sortKey: string;
    sortDirection: "asc" | "desc";
    onSort: (key: SortKey) => void;
    expanded: string | null;
    onToggle: (key: string) => void;
    renderDetail: (row: ExplorerRowDto) => React.ReactNode;
}

const num = (v: number | null | undefined, unit: string, compact = false): React.ReactNode =>
    formatMetric(v, unit, compact);

const signed = (v: number | null | undefined): React.ReactNode => (
    <Box component="span" sx={{ color: signColor(v) }}>
        {formatMetric(v, "currency", true)}
    </Box>
);

const pct = (v: number | null | undefined, digits = 1): React.ReactNode =>
    v === null || v === undefined || !Number.isFinite(v) ? DASH : formatPercent(v, digits);

export const buildColumns = (
    result: ExplorerResultDto,
    conversionLabel: string,
    rpmLabel: string
): IColumn[] => {
    const dims = result.dimensions;
    const cols: IColumn[] = dims.map((d, i) => ({
        key: `dim${i}`,
        label: d.label,
        align: "left",
        width: 200,
        render: (r) => (
            <Box
                sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                title={r.dimValues[i] || DASH}
            >
                {r.dimValues[i] || DASH}
            </Box>
        ),
    }));

    cols.push(
        {
            key: "cost",
            label: "Cost",
            sortKey: "cost",
            align: "right",
            title: "Media spend, and this slice's share of the visible total",
            render: (r) => (
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.75, alignItems: "baseline" }}>
                    <span>{num(r.cost, "currency", true)}</span>
                    <Box component="span" sx={{ fontSize: 10, color: "text.disabled", width: 40, textAlign: "left" }}>
                        {r.costShare > 0 ? formatPercent(r.costShare, 0) : ""}
                    </Box>
                </Box>
            ),
        },
        {
            key: "impressions",
            label: "Impressions",
            sortKey: "impressions",
            align: "right",
            render: (r) => num(r.impressions, "count", true),
        },
        { key: "cpm", label: "CPM", sortKey: "cpm", align: "right", render: (r) => num(r.cpm, "currency") },
        { key: "rpm", label: rpmLabel, sortKey: "rpm", align: "right", render: (r) => num(r.rpm, "currency") },
        {
            key: "edgePer1k",
            label: "Edge / 1K",
            sortKey: "edgePer1k",
            align: "right",
            title: "RPM minus CPM — profit per thousand impressions",
            render: (r) => signed(r.edgePer1k),
        },
        {
            key: "conversions",
            label: conversionLabel,
            sortKey: "conversions",
            align: "right",
            render: (r) => num(r.conversions, "count"),
        },
        { key: "profit", label: "Profit", sortKey: "profit", align: "right", render: (r) => signed(r.profit) },
        { key: "cpa", label: "CPA", sortKey: "cpa", align: "right", render: (r) => num(r.cpa, "currency") },
        { key: "roi", label: "ROI", sortKey: "roi", align: "right", render: (r) => pct(r.roi, 0) },
        { key: "roas", label: "ROAS", sortKey: "roas", align: "right", render: (r) => num(r.roas, "multiple") },
        { key: "margin", label: "Margin", sortKey: "margin", align: "right", render: (r) => pct(r.margin, 0) },
        { key: "grossRevenue", label: "Gross Revenue", sortKey: "grossRevenue", align: "right", render: (r) => num(r.grossRevenue, "currency") },
        { key: "takeRate", label: "Take Rate", sortKey: "takeRate", align: "right", render: (r) => pct(r.takeRate, 0) },
        {
            key: "conversionRate",
            label: "CR",
            align: "right",
            title: "Attributed conversions / impressions",
            render: (r) => pct(r.conversionRate),
        },
        {
            key: "visitRate",
            label: "VR",
            align: "right",
            title: "Visits / impressions",
            render: (r) => pct(r.visitRate),
        }
    );

    if (result.view === "verification") {
        cols.push(
            {
                key: "verifiedInstalls",
                label: "Verified",
                align: "right",
                render: (r) => num(r.verifiedInstalls, "count"),
            },
            {
                key: "verificationRate",
                label: "Verification",
                align: "right",
                title: "Verified installs as a share of attributed conversions",
                render: (r) => pct(r.verification.verificationRate, 0),
            },
            {
                key: "dropOff",
                label: "Drop-off",
                align: "right",
                title: "Attributed conversions that never verified. Never negative — an excess shows as over-verification.",
                render: (r) => {
                    const v = r.verification.dropOff;
                    const heavy = v !== null && v >= 0.5;
                    return (
                        <Box component="span" sx={{ color: heavy ? "error.main" : "text.primary", fontWeight: heavy ? 600 : 400 }}>
                            {pct(v, 0)}
                        </Box>
                    );
                },
            },
            {
                key: "revenueGap",
                label: "Revenue Gap",
                align: "right",
                title: "Real revenue minus attributed revenue",
                render: (r) => signed(r.verification.revenueGap),
            },
            {
                key: "profitGap",
                label: "Profit Gap",
                align: "right",
                render: (r) => signed(r.verification.profitGap),
            }
        );
    }

    // Suppressed when a grouping dimension is the publisher axis: the split would be tautological.
    if (result.hasContext) {
        const axis = result.publisherAxis.label.toLowerCase();
        cols.push(
            {
                key: "coverage",
                label: "Coverage",
                align: "right",
                title: `How many ${axis}s carry this slice at all`,
                render: (r) => (r.context ? `${r.context.coverage}` : DASH),
            },
            {
                key: "share",
                label: "Profitable Share",
                align: "right",
                title: `${result.publisherAxis.label}s where this slice makes money, of those where it can be measured`,
                render: (r) =>
                    r.context && r.context.share !== null
                        ? `${r.context.profitableCount} of ${r.context.usableCount} (${formatPercent(r.context.share, 0)})`
                        : DASH,
            },
        );
    }

    return cols;
};

const ExplorerTable = ({
    result,
    conversionLabel,
    rpmLabel,
    sortKey,
    sortDirection,
    onSort,
    expanded,
    onToggle,
    renderDetail,
}: IProps): JSX.Element => {
    const columns = React.useMemo(
        () => buildColumns(result, conversionLabel, rpmLabel),
        [result, conversionLabel, rpmLabel]
    );

    /** A detail row spans every column, so on a table wider than the screen it stretches off to the */
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const [viewportWidth, setViewportWidth] = React.useState(0);
    React.useEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;
        const ro = new ResizeObserver(() => setViewportWidth(el.clientWidth));
        ro.observe(el);
        setViewportWidth(el.clientWidth);
        return () => ro.disconnect();
    }, []);

    return (
        <TableContainer
            ref={containerRef}
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: "62vh" }}
        >
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: 34, p: 0 }} />
                        {columns.map((c) => (
                            <TableCell
                                key={c.key}
                                align={c.align ?? "right"}
                                sx={{ whiteSpace: "nowrap", fontWeight: 600 }}
                            >
                                {c.sortKey ? (
                                    <Tooltip title={c.title ?? ""} arrow disableHoverListener={!c.title}>
                                        <Box
                                            component="span"
                                            onClick={() => onSort(c.sortKey as SortKey)}
                                            sx={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 0.25,
                                                cursor: "pointer",
                                                userSelect: "none",
                                                color: sortKey === c.sortKey ? "text.primary" : "inherit",
                                                "&:hover": { color: "primary.main" },
                                            }}
                                        >
                                            {c.label}
                                            {/* Same caret vocabulary the KPI deltas use, so a direction reads the same everywhere. */}
                                            <Box
                                                component="span"
                                                sx={{
                                                    fontSize: 9,
                                                    lineHeight: 1,
                                                    visibility: sortKey === c.sortKey ? "visible" : "hidden",
                                                }}
                                            >
                                                {sortDirection === "asc" ? "▲" : "▼"}
                                            </Box>
                                        </Box>
                                    </Tooltip>
                                ) : (
                                    <Tooltip title={c.title ?? ""} arrow disableHoverListener={!c.title}>
                                        <Box component="span" sx={{ cursor: c.title ? "help" : "inherit" }}>
                                            {c.label}
                                        </Box>
                                    </Tooltip>
                                )}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {/* Totals first, from summed numerators and denominators — never averaged rows. */}
                    <TableRow sx={{ bgcolor: "action.hover", "& td": { fontWeight: 600 } }}>
                        <TableCell sx={{ p: 0 }} />
                        {columns.map((c, i) => (
                            <TableCell key={c.key} align={c.align ?? "right"} sx={{ whiteSpace: "nowrap" }}>
                                {i === 0 ? (
                                    <>
                                        TOTAL{" "}
                                        {/* Counts every qualifying row, including any cut by the display cap. */}
                                        <Box
                                            component="span"
                                            sx={{ fontWeight: 400, fontSize: 11, color: "text.secondary" }}
                                        >
                                            · {formatNumber(result.totalRows)} row
                                            {result.totalRows === 1 ? "" : "s"}
                                        </Box>
                                    </>
                                ) : i < result.dimensions.length ? (
                                    ""
                                ) : (
                                    c.render(result.totals)
                                )}
                            </TableCell>
                        ))}
                    </TableRow>

                    {result.rows.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                    No slices in this view. Lower the minimum impressions or widen the scope.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}

                    {result.rows.map((r) => {
                        const key = r.dimValues.join("\t");
                        const isOpen = expanded === key;
                        return (
                            <React.Fragment key={key}>
                                <TableRow
                                    hover
                                    onClick={() => onToggle(key)}
                                    sx={{ cursor: "pointer", ...(isOpen ? { bgcolor: "action.selected" } : {}) }}
                                >
                                    <TableCell sx={{ p: 0, textAlign: "center" }}>
                                        <KeyboardArrowRight
                                            sx={{
                                                fontSize: 16,
                                                color: "text.disabled",
                                                transition: "transform 120ms",
                                                transform: isOpen ? "rotate(90deg)" : "none",
                                            }}
                                        />
                                    </TableCell>
                                    {columns.map((c) => (
                                        <TableCell
                                            key={c.key}
                                            align={c.align ?? "right"}
                                            sx={{
                                                whiteSpace: "nowrap",
                                                fontVariantNumeric: c.align === "left" ? "normal" : "tabular-nums",
                                            }}
                                        >
                                            {c.render(r)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                                {isOpen && (
                                    <TableRow>
                                        <TableCell colSpan={columns.length + 1} sx={{ p: 0, bgcolor: "action.hover" }}>
                                            <Box
                                                sx={{
                                                    position: "sticky",
                                                    left: 0,
                                                    width: viewportWidth || "100%",
                                                }}
                                            >
                                                {renderDetail(r)}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default ExplorerTable;
